/**
 * Payment Processor Settings Component
 * Allows organizations to connect/disconnect payment processors
 * and configure manual payment methods
 */

import { useState, useEffect } from 'react'
import { integrations } from '../../lib/api'
import { useToast } from '../../contexts/ToastContext'
import PropTypes from 'prop-types'

// Payment processor icons - simple letter/logo designs with perfect centering
const ProcessorIcon = ({ provider }) => {
  const icons = {
    // Stripe - white S on purple
    stripe: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <rect width="32" height="32" rx="6" fill="#635BFF" />
        <text x="16" y="16" textAnchor="middle" dominantBaseline="central" fill="white" fontSize="18" fontWeight="bold" fontFamily="system-ui">S</text>
      </svg>
    ),
    // Square - Black background with white square
    square: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <rect width="32" height="32" rx="6" fill="#000" />
        <rect x="9" y="9" width="14" height="14" rx="2" stroke="white" strokeWidth="2.5" fill="none"/>
      </svg>
    ),
    // PayPal - white P on blue
    paypal: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <rect width="32" height="32" rx="6" fill="#003087" />
        <text x="16" y="16" textAnchor="middle" dominantBaseline="central" fill="white" fontSize="18" fontWeight="bold" fontFamily="system-ui">P</text>
      </svg>
    ),
    // QuickBooks - Green background with white QB
    quickbooks: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <rect width="32" height="32" rx="6" fill="#2CA01C" />
        <text x="16" y="16" textAnchor="middle" dominantBaseline="central" fill="white" fontSize="11" fontWeight="bold" fontFamily="system-ui">QB</text>
      </svg>
    ),
    // Venmo - white V on blue
    venmo: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <rect width="32" height="32" rx="6" fill="#008CFF" />
        <text x="16" y="16" textAnchor="middle" dominantBaseline="central" fill="white" fontSize="18" fontWeight="bold" fontFamily="system-ui">V</text>
      </svg>
    ),
    // Zelle - Purple with Z
    zelle: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <rect width="32" height="32" rx="6" fill="#6D1ED4" />
        <text x="16" y="16" textAnchor="middle" dominantBaseline="central" fill="white" fontSize="18" fontWeight="bold" fontFamily="system-ui">Z</text>
      </svg>
    ),
    // Cash App - Green with white $
    cashapp: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <rect width="32" height="32" rx="6" fill="#00D632" />
        <text x="16" y="16" textAnchor="middle" dominantBaseline="central" fill="white" fontSize="18" fontWeight="bold" fontFamily="system-ui">$</text>
      </svg>
    ),
    // Apple Cash - white Apple logo on black (centered)
    applecash: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <rect width="32" height="32" rx="6" fill="#000" />
        <path d="M21.5 21.4c-.4.8-.8 1.5-1.4 2.2-.5.5-1.1.8-1.6.8-.4 0-.9-.1-1.4-.4-.5-.2-1-.4-1.4-.4-.5 0-.9.1-1.4.4-.5.2-.9.4-1.3.4-.6 0-1.3-.4-1.8-1-.6-.7-1-1.4-1.4-2.3-.4-1-.6-2-.6-2.9 0-1.1.2-2 .7-2.7.4-.6.9-1 1.4-1.3.6-.3 1.2-.5 1.9-.5.5 0 1 .1 1.6.4.5.2.9.4 1.1.4.1 0 .5-.1 1.1-.4.5-.2 1.1-.4 1.5-.4 1.2.1 2 .5 2.6 1.3-1 .6-1.5 1.5-1.5 2.6 0 .9.3 1.6.9 2.2.3.3.6.5.9.6l-.3.6zM18.3 9.5c0 .6-.2 1.3-.7 1.9-.6.7-1.3 1.1-2.1 1v-.2c0-.6.3-1.3.7-1.8.2-.3.5-.5.9-.7.4-.2.7-.3 1.1-.3l.1.1z" fill="white"/>
      </svg>
    ),
    // Cash/Check - Green with dollar bill
    cash: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <rect width="32" height="32" rx="6" fill="#16A34A" />
        <rect x="6" y="10" width="20" height="12" rx="2" fill="white"/>
        <circle cx="16" cy="16" r="3" stroke="#16A34A" strokeWidth="1.5" fill="none"/>
        <text x="16" y="16" textAnchor="middle" dominantBaseline="central" fill="#16A34A" fontSize="6" fontWeight="bold" fontFamily="system-ui">$</text>
      </svg>
    ),
  }

  return icons[provider] || (
    <div className="w-8 h-8 bg-slate-200 rounded-md flex items-center justify-center">
      <span className="text-xs font-bold text-slate-500">{provider?.[0]?.toUpperCase()}</span>
    </div>
  )
}

ProcessorIcon.propTypes = {
  provider: PropTypes.string.isRequired,
}

export default function PaymentProcessorSettings({ organizationId, canManage = true }) {
  const toast = useToast()
  const [loading, setLoading] = useState(true)

  // Payment method settings (stored in org settings)
  const [manualMethods, setManualMethods] = useState({
    venmo_handle: '',
    zelle_email: '',
    cashapp_tag: '',
    applecash_phone: '',
    stripe_link: '',
    square_link: '',
    paypal_link: '',
    quickbooks_link: '',
    cash_enabled: true,
  })

  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadPaymentConfig()
  }, [organizationId])

  const loadPaymentConfig = async () => {
    try {
      setLoading(true)
      const config = await integrations.getPaymentConfig(organizationId)
      setManualMethods({
        venmo_handle: config.venmo_handle || '',
        zelle_email: config.zelle_email || '',
        cashapp_tag: config.cashapp_tag || '',
        paypal_link: config.paypal_link || '',
        quickbooks_link: config.quickbooks_link || '',
        cash_enabled: config.cash_enabled !== false,
        stripe_link: config.stripe_link || '',
        square_link: config.square_link || '',
        applecash_phone: config.applecash_phone || '',
      })
    } catch (error) {
      console.error('Error loading payment config:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveManualMethods = async () => {
    try {
      setSaving(true)
      await integrations.updatePaymentConfig(organizationId, manualMethods)
      toast.success('Payment methods saved successfully')
    } catch (error) {
      console.error('Error saving payment config:', error)
      toast.error('Failed to save payment methods')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="card">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-slate-200 rounded w-1/3"></div>
          <div className="h-20 bg-slate-100 rounded"></div>
          <div className="h-20 bg-slate-100 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Payment Methods */}
      <div className="card">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Payment Methods
        </h3>
        <p className="text-sm text-slate-500 mb-6">
          Add your payment links and handles. Players will see these options when paying their dues.
        </p>

        <div className="space-y-4">
          {/* Stripe Payment Link */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
            <div className="flex items-center gap-4">
              <ProcessorIcon provider="stripe" />
              <div>
                <h4 className="font-medium text-slate-900">Stripe</h4>
                <p className="text-sm text-slate-500">Create a payment link at stripe.com/payments/payment-links</p>
              </div>
            </div>
            <input
              type="url"
              placeholder="https://buy.stripe.com/..."
              value={manualMethods.stripe_link || ''}
              onChange={(e) => setManualMethods(m => ({ ...m, stripe_link: e.target.value }))}
              className="input w-48"
              disabled={!canManage}
            />
          </div>

          {/* Square Payment Link */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
            <div className="flex items-center gap-4">
              <ProcessorIcon provider="square" />
              <div>
                <h4 className="font-medium text-slate-900">Square</h4>
                <p className="text-sm text-slate-500">Create a checkout link at squareup.com</p>
              </div>
            </div>
            <input
              type="url"
              placeholder="https://square.link/..."
              value={manualMethods.square_link || ''}
              onChange={(e) => setManualMethods(m => ({ ...m, square_link: e.target.value }))}
              className="input w-48"
              disabled={!canManage}
            />
          </div>

          {/* PayPal.me */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
            <div className="flex items-center gap-4">
              <ProcessorIcon provider="paypal" />
              <div>
                <h4 className="font-medium text-slate-900">PayPal.me</h4>
                <p className="text-sm text-slate-500">Share your PayPal.me link</p>
              </div>
            </div>
            <input
              type="text"
              placeholder="paypal.me/YourName"
              value={manualMethods.paypal_link || ''}
              onChange={(e) => setManualMethods(m => ({ ...m, paypal_link: e.target.value }))}
              className="input w-48"
              disabled={!canManage}
            />
          </div>

          {/* QuickBooks Payments */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
            <div className="flex items-center gap-4">
              <ProcessorIcon provider="quickbooks" />
              <div>
                <h4 className="font-medium text-slate-900">QuickBooks Payments</h4>
                <p className="text-sm text-slate-500">Create an invoice link in QuickBooks</p>
              </div>
            </div>
            <input
              type="url"
              placeholder="https://..."
              value={manualMethods.quickbooks_link || ''}
              onChange={(e) => setManualMethods(m => ({ ...m, quickbooks_link: e.target.value }))}
              className="input w-48"
              disabled={!canManage}
            />
          </div>

          {/* Venmo */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
            <div className="flex items-center gap-4">
              <ProcessorIcon provider="venmo" />
              <div>
                <h4 className="font-medium text-slate-900">Venmo</h4>
                <p className="text-sm text-slate-500">Share your Venmo handle (e.g., @MyLeague)</p>
              </div>
            </div>
            <input
              type="text"
              placeholder="@YourHandle"
              value={manualMethods.venmo_handle}
              onChange={(e) => setManualMethods(m => ({ ...m, venmo_handle: e.target.value }))}
              className="input w-48"
              disabled={!canManage}
            />
          </div>

          {/* Zelle */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
            <div className="flex items-center gap-4">
              <ProcessorIcon provider="zelle" />
              <div>
                <h4 className="font-medium text-slate-900">Zelle</h4>
                <p className="text-sm text-slate-500">Share your Zelle email or phone</p>
              </div>
            </div>
            <input
              type="text"
              placeholder="payments@yourleague.com"
              value={manualMethods.zelle_email}
              onChange={(e) => setManualMethods(m => ({ ...m, zelle_email: e.target.value }))}
              className="input w-48"
              disabled={!canManage}
            />
          </div>

          {/* Cash App */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
            <div className="flex items-center gap-4">
              <ProcessorIcon provider="cashapp" />
              <div>
                <h4 className="font-medium text-slate-900">Cash App</h4>
                <p className="text-sm text-slate-500">Share your Cash App $cashtag</p>
              </div>
            </div>
            <input
              type="text"
              placeholder="$YourCashtag"
              value={manualMethods.cashapp_tag}
              onChange={(e) => setManualMethods(m => ({ ...m, cashapp_tag: e.target.value }))}
              className="input w-48"
              disabled={!canManage}
            />
          </div>

          {/* Apple Cash */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
            <div className="flex items-center gap-4">
              <ProcessorIcon provider="applecash" />
              <div>
                <h4 className="font-medium text-slate-900">Apple Cash</h4>
                <p className="text-sm text-slate-500">Share your Apple Cash phone number</p>
              </div>
            </div>
            <input
              type="tel"
              placeholder="(555) 123-4567"
              value={manualMethods.applecash_phone}
              onChange={(e) => setManualMethods(m => ({ ...m, applecash_phone: e.target.value }))}
              className="input w-48"
              disabled={!canManage}
            />
          </div>

          {/* Cash/Check */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
            <div className="flex items-center gap-4">
              <ProcessorIcon provider="cash" />
              <div>
                <h4 className="font-medium text-slate-900">Cash/Check</h4>
                <p className="text-sm text-slate-500">Accept in-person cash or check payments</p>
              </div>
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={manualMethods.cash_enabled}
                onChange={(e) => setManualMethods(m => ({ ...m, cash_enabled: e.target.checked }))}
                className="rounded border-slate-300 text-ice-600 focus:ring-ice-500"
                disabled={!canManage}
              />
              <span className="text-sm text-slate-600">Enabled</span>
            </label>
          </div>
        </div>

        {canManage && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSaveManualMethods}
              disabled={saving}
              className="btn-primary"
            >
              {saving ? 'Saving...' : 'Save Payment Methods'}
            </button>
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="card bg-slate-50 border-slate-200">
        <h4 className="font-medium text-slate-900 mb-2">How Payment Collection Works</h4>
        <ul className="text-sm text-slate-600 space-y-2">
          <li>
            <strong>Payment links (Stripe, Square, PayPal):</strong> Players click the link to pay with card. Money goes directly to your account. You manually mark payments as received.
          </li>
          <li>
            <strong>P2P apps (Venmo, Zelle, Cash App, Apple Cash):</strong> Players see your handle/info and pay you directly. You manually mark payments as received.
          </li>
          <li>
            <strong>Multiple methods:</strong> Enable as many methods as you like. Players can choose how to pay.
          </li>
        </ul>
      </div>
    </div>
  )
}

PaymentProcessorSettings.propTypes = {
  organizationId: PropTypes.number.isRequired,
  canManage: PropTypes.bool,
}
